import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { getCompany } from '../utils/tokenManager';
import {
  TabNav,
  OverviewTab,
  ReceiptScopeTab,
  ReceiptQuantityTab,
  AmountThresholdsTab,
  ReviewModeTab,
  RewardsTab,
  UserLimitsTab,
  TimeRulesTab
} from '../components/rules';
import '../components/rules/Rules.css';
import './RulesConfig.css';

function RulesConfig() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('scope'); // Start with Receipt Scope
  const [config, setConfig] = useState(null);
  const [voucherSchemas, setVoucherSchemas] = useState([]);
  const [tokenList, setTokenList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Fetch campaign configuration
  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/logic/config');
      if (response.data.success) {
        setConfig(response.data.data);
      }
    } catch (err) {
      setError('Failed to fetch configuration');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Silent refresh - update config without showing loading state
  const refreshConfig = useCallback(async () => {
    try {
      const response = await api.get('/logic/config');
      if (response.data.success) {
        setConfig(response.data.data);
      }
    } catch (err) {
      console.error('Failed to refresh configuration:', err);
    }
  }, []);

  // Fetch voucher schemas for rewards tab
  const fetchVoucherSchemas = useCallback(async () => {
    try {
      const companyId = getCompany();
      const params = { page: 1, size: 1000 };
      // Add company filter if available (format: filter=campaign.companyId:xxx)
      if (companyId) {
        params.filter = `campaign.companyId:${companyId}`;
      }
      const response = await api.get('/vio/campaign/voucherSchema/published/list', {
        params
      });
      // VIO API returns: { status: "Success", message: { data: [...] } }
      if (response.data?.status === 'Success' && response.data?.message?.data) {
        // Map to a simpler format for the dropdown
        const schemas = response.data.message.data.map(item => ({
          id: item.campaignVoucherSchemaId,
          schemaId: item.voucherSchema?.voucherSchemaId,
          name: item.voucherSchema?.voucherSchemaName?.en || item.voucherSchema?.voucherSchemaName || 'Unnamed Voucher',
          imageUrl: item.voucherSchema?.image?.imageUrl,
          value: item.voucherSchema?.value,
          isLimited: item.voucherSchema?.isLimited,
          quantity: item.quantity,
          expiryEnd: item.voucherSchema?.expiryEndDatetime
        }));
        setVoucherSchemas(schemas);
      }
    } catch (err) {
      console.error('Failed to fetch voucher schemas:', err);
    }
  }, []);

  // Fetch token list for rewards tab
  const fetchTokenList = useCallback(async () => {
    try {
      const response = await api.get('/vio/token/list', {
        params: { page: 1, size: 1000 }
      });
      // VIO API returns: { status: "Success", message: { data: [...] } }
      if (response.data?.status === 'Success' && response.data?.message?.data) {
        // Map to a simpler format for the dropdown
        const tokens = response.data.message.data.map(item => ({
          id: item.dbTokId,
          name: item.tokenName,
          symbol: item.tokenSymbol,
          balance: item.balance,
          decimal: item.decimal,
          logoUrl: item.logo?.imageUrl
        }));
        setTokenList(tokens);
      }
    } catch (err) {
      console.error('Failed to fetch token list:', err);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
    fetchVoucherSchemas();
    fetchTokenList();
  }, [fetchConfig, fetchVoucherSchemas, fetchTokenList]);

  // Update configuration (partial updates)
  const handleUpdateConfig = async (updates) => {
    try {
      const response = await api.put('/logic/config', updates);
      if (response.data.success) {
        setConfig(response.data.data);
      }
      return response.data;
    } catch (err) {
      console.error('Failed to update config:', err);
      throw err;
    }
  };

  // Tenant operations
  const handleAddTenant = async (tenant) => {
    try {
      const response = await api.post('/logic/tenants', tenant);
      if (response.data.success) {
        await refreshConfig();
      }
      return response.data;
    } catch (err) {
      console.error('Failed to add tenant:', err);
      throw err;
    }
  };

  const handleDeleteTenant = async (id) => {
    try {
      const response = await api.delete(`/logic/tenants/${id}`);
      if (response.data.success) {
        await refreshConfig();
      }
      return response.data;
    } catch (err) {
      console.error('Failed to delete tenant:', err);
      throw err;
    }
  };

  // Voucher pool operations
  const handleAddVoucher = async (voucher) => {
    try {
      const response = await api.post('/logic/config/voucher-pool', voucher);
      if (response.data.success) {
        await refreshConfig();
      }
      return response.data;
    } catch (err) {
      console.error('Failed to add voucher:', err);
      throw err;
    }
  };

  const handleUpdateVoucher = async (id, voucher) => {
    try {
      const response = await api.put(`/logic/config/voucher-pool/${id}`, voucher);
      if (response.data.success) {
        await refreshConfig();
      }
      return response.data;
    } catch (err) {
      console.error('Failed to update voucher:', err);
      throw err;
    }
  };

  const handleDeleteVoucher = async (id) => {
    try {
      const response = await api.delete(`/logic/config/voucher-pool/${id}`);
      if (response.data.success) {
        await refreshConfig();
      }
      return response.data;
    } catch (err) {
      console.error('Failed to delete voucher:', err);
      throw err;
    }
  };

  // Token rule operations
  const handleAddTokenRule = async (rule) => {
    try {
      const response = await api.post('/logic/config/token-rules', rule);
      if (response.data.success) {
        await refreshConfig();
      }
      return response.data;
    } catch (err) {
      console.error('Failed to add token rule:', err);
      throw err;
    }
  };

  const handleUpdateTokenRule = async (id, rule) => {
    try {
      const response = await api.put(`/logic/config/token-rules/${id}`, rule);
      if (response.data.success) {
        await refreshConfig();
      }
      return response.data;
    } catch (err) {
      console.error('Failed to update token rule:', err);
      throw err;
    }
  };

  const handleDeleteTokenRule = async (id) => {
    try {
      const response = await api.delete(`/logic/config/token-rules/${id}`);
      if (response.data.success) {
        await refreshConfig();
      }
      return response.data;
    } catch (err) {
      console.error('Failed to delete token rule:', err);
      throw err;
    }
  };

  // Get tab steps configuration
  const getTabSteps = () => {
    return [
      { id: 'scope', number: 1, label: 'Receipt Scope', shortLabel: 'Receipt Scope' },
      { id: 'quantity', number: 2, label: 'Quantity', shortLabel: 'Quantity' },
      { id: 'thresholds', number: 3, label: 'Thresholds', shortLabel: 'Thresholds' },
      { id: 'review', number: 4, label: 'Review Mode', shortLabel: 'Review Mode' },
      { id: 'rewards', number: 5, label: 'Rewards', shortLabel: 'Rewards' },
      { id: 'limits', number: 6, label: 'User Limits', shortLabel: 'User Limits' },
      { id: 'time', number: 7, label: 'Time Rules', shortLabel: 'Time Rules' }
    ];
  };

  // Check if a step is completed based on config
  const isStepCompleted = (stepId) => {
    if (!config) return false;
    
    switch (stepId) {
      case 'scope':
        // Check if tenants are configured
        return config.eligibleTenants && config.eligibleTenants.length > 0;
      
      case 'quantity':
        // Check if quantity rules are set
        return config.receiptQuantityRules && 
               (config.receiptQuantityRules.maxTotalReceipts !== undefined ||
                config.receiptQuantityRules.categoryLimits);
      
      case 'thresholds':
        // Check if amount thresholds are configured
        return config.amountThresholds && 
               Object.keys(config.amountThresholds).length > 0;
      
      case 'review':
        // Check if review mode is configured
        return config.reviewMode !== undefined;
      
      case 'rewards':
        // Check if rewards are configured
        return config.rewards && 
               ((config.rewards.voucherPool && config.rewards.voucherPool.length > 0) ||
                (config.rewards.tokenRules && config.rewards.tokenRules.length > 0));
      
      case 'limits':
        // Check if user limits are configured
        return config.userLimits && 
               (config.userLimits.maxClaimsPerUser !== undefined ||
                config.userLimits.maxRewardsPerUser !== undefined);
      
      case 'time':
        // Check if time rules are configured
        return config.timeRules && 
               (config.timeRules.campaignStart !== null ||
                config.timeRules.campaignEnd !== null ||
                config.timeRules.receiptDateBuffer !== undefined);
      
      default:
        return false;
    }
  };

  const getCurrentStepIndex = () => {
    return getTabSteps().findIndex(step => step.id === activeTab);
  };

  const handleNext = () => {
    const steps = getTabSteps();
    const currentIndex = getCurrentStepIndex();
    if (currentIndex < steps.length - 1) {
      setActiveTab(steps[currentIndex + 1].id);
    } else if (isLastStep()) {
      // On the last step, clicking Finish shows success modal
      handleFinish();
    }
  };

  const handleFinish = async () => {
    // Show success modal
    setShowSuccessModal(true);
    
    // Optionally, you can trigger a final save here if needed
    // await handleUpdateConfig({});
  };

  const closeSuccessModal = () => {
    setShowSuccessModal(false);
    navigate('/submissions');
  };

  const handleBack = () => {
    const steps = getTabSteps();
    const currentIndex = getCurrentStepIndex();
    if (currentIndex > 0) {
      setActiveTab(steps[currentIndex - 1].id);
    }
  };

  const handleCancel = () => {
    navigate('/submissions');
  };

  const isFirstStep = () => {
    return getCurrentStepIndex() === 0;
  };

  const isLastStep = () => {
    return getCurrentStepIndex() === getTabSteps().length - 1;
  };

  // Render the active tab
  const renderTab = () => {
    const commonProps = {
      config,
      onUpdate: handleUpdateConfig
    };

    switch (activeTab) {
      case 'scope':
        return (
          <ReceiptScopeTab
            {...commonProps}
            onAddTenant={handleAddTenant}
            onDeleteTenant={handleDeleteTenant}
          />
        );
      case 'quantity':
        return <ReceiptQuantityTab {...commonProps} />;
      case 'thresholds':
        return <AmountThresholdsTab {...commonProps} />;
      case 'review':
        return <ReviewModeTab {...commonProps} />;
      case 'rewards':
        return (
          <RewardsTab
            {...commonProps}
            voucherSchemas={voucherSchemas}
            tokenList={tokenList}
            onAddVoucher={handleAddVoucher}
            onUpdateVoucher={handleUpdateVoucher}
            onDeleteVoucher={handleDeleteVoucher}
            onAddTokenRule={handleAddTokenRule}
            onUpdateTokenRule={handleUpdateTokenRule}
            onDeleteTokenRule={handleDeleteTokenRule}
          />
        );
      case 'limits':
        return <UserLimitsTab {...commonProps} />;
      case 'time':
        return <TimeRulesTab {...commonProps} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="rules-config">
        <div className="loading-state">
          <div className="loading-spinner-icon"></div>
          <p>Loading configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rules-config">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <button className="breadcrumb-link" onClick={() => navigate('/submissions')}>
          Receipt Ops
        </button>
        <span className="breadcrumb-separator">&gt;</span>
        <span className="breadcrumb-current">Rules Configuration</span>
      </div>

      {/* Page Header */}
      <div className="page-header-wizard">
        <h1>Rules Configuration</h1>
        {config?.updatedAt && (
          <div className="last-updated">
            Last updated: {new Date(config.updatedAt).toLocaleString()}
          </div>
        )}
      </div>

      {/* Warning Message */}
      {config && (() => {
        const missingSteps = getTabSteps().filter(step => !isStepCompleted(step.id));
        const totalSteps = getTabSteps().length;
        const completedCount = totalSteps - missingSteps.length;
        const completeness = Math.round((completedCount / totalSteps) * 100);
        
        if (missingSteps.length > 0) {
          return (
            <div className="warning-message">
              <span className="warning-icon">⚠️</span>
              <span>
                {missingSteps.length} configuration{missingSteps.length > 1 ? 's are' : ' is'} missing: {' '}
                {missingSteps.map((step, index) => (
                  <React.Fragment key={step.id}>
                    <button 
                      className="missing-config-link"
                      onClick={() => setActiveTab(step.id)}
                    >
                      {step.label}
                    </button>
                    {index < missingSteps.length - 1 && ', '}
                  </React.Fragment>
                ))}
                . Configuration completeness {completeness}%
              </span>
            </div>
          );
        }
        return null;
      })()}

      {error && <div className="error-message">{error}</div>}

      {/* Stepper Navigation */}
      <div className="stepper-container">
        {getTabSteps().map((step, index) => {
          const currentIndex = getCurrentStepIndex();
          const isActive = index === currentIndex;
          const isCompleted = isStepCompleted(step.id);
          const isVisited = index < currentIndex;
          
          return (
            <React.Fragment key={step.id}>
              <div 
                className={`stepper-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                onClick={() => setActiveTab(step.id)}
              >
                <div className="stepper-label">
                  {step.number}. {step.shortLabel}
                </div>
              </div>
              {index < getTabSteps().length - 1 && (
                <div className={`stepper-line ${isCompleted ? 'completed' : ''}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="wizard-content">
        {renderTab()}
      </div>

      {/* Action Buttons */}
      <div className="wizard-actions">
        {isFirstStep() ? (
          <button className="btn-wizard btn-cancel" onClick={handleCancel}>
            Cancel
          </button>
        ) : (
          <button className="btn-wizard btn-back" onClick={handleBack}>
            Back
          </button>
        )}
        <button className="btn-wizard btn-next" onClick={handleNext}>
          {isLastStep() ? 'Finish' : 'Next'}
        </button>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="modal-overlay" onClick={closeSuccessModal}>
          <div className="modal-content success-modal" onClick={e => e.stopPropagation()}>
            <div className="success-icon">✓</div>
            <h2>Configuration Saved!</h2>
            <p>Your rules configuration has been saved successfully.</p>
            <button className="btn-success-ok" onClick={closeSuccessModal}>
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default RulesConfig;
